/**
 * OnboardingDebugOverlay - Temporary visual debug panel for first-run flow
 * Shows all critical flags in real-time
 * Remove after debugging is complete
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bug, X, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { 
  isTourActiveGlobal, 
  isOnboardingTransitioning, 
  isTourRequested 
} from '@/hooks/useTourController';
import { getLocalStorageState, computeFirstRunStage } from '@/hooks/useFirstRunState';

// Only show in development or if enabled via localStorage
const SHOW_DEBUG = () => {
  try {
    return localStorage.getItem('therappi-debug-onboarding') === 'true';
  } catch {
    return false;
  }
};

export const OnboardingDebugOverlay: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [state, setState] = useState({
    userId: null as string | null,
    isAuthenticated: false,
    onboardingCompleted: null as boolean | null,
    welcomeSeen: false,
    tourSeen: false,
    tourActive: false,
    tourRequested: false,
    transitioning: false,
    computedStage: 'loading',
    timestamp: new Date().toISOString()
  });

  const refreshState = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const localState = getLocalStorageState();
    
    let onboardingCompleted: boolean | null = null;
    if (session?.user) {
      const { data } = await supabase
        .from('profiles')
        .select('onboarding_completed')
        .eq('user_id', session.user.id)
        .maybeSingle();
      onboardingCompleted = data?.onboarding_completed ?? null;
    }

    const newState = {
      userId: session?.user?.id ?? null,
      isAuthenticated: !!session?.user,
      onboardingCompleted,
      welcomeSeen: localState.welcomeSeen,
      tourSeen: localState.tourSeen,
      tourActive: isTourActiveGlobal(),
      tourRequested: isTourRequested(),
      transitioning: isOnboardingTransitioning(),
      computedStage: computeFirstRunStage(
        onboardingCompleted, 
        false, 
        !!session?.user
      ),
      timestamp: new Date().toISOString()
    };

    setState(newState);
    console.log('[DEBUG OVERLAY] State refresh:', newState);
  };

  useEffect(() => {
    if (!SHOW_DEBUG()) return;
    
    refreshState();
    
    // Refresh every 2 seconds
    const interval = setInterval(refreshState, 2000);
    
    // Also refresh on auth change
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      setTimeout(refreshState, 100);
    });

    return () => {
      clearInterval(interval);
      subscription.unsubscribe();
    };
  }, []);

  if (!SHOW_DEBUG()) return null;

  const getStatusColor = (value: boolean | null) => {
    if (value === null) return 'bg-yellow-500';
    return value ? 'bg-green-500' : 'bg-red-500';
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'loading': return 'text-yellow-400';
      case 'onboarding': return 'text-orange-400';
      case 'welcome': return 'text-blue-400';
      case 'tour': return 'text-purple-400';
      case 'app': return 'text-green-400';
      default: return 'text-white';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed bottom-20 right-2 z-[10000] pointer-events-auto"
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-gray-900 text-white p-2 rounded-lg shadow-lg border border-gray-700 flex items-center gap-2"
      >
        <Bug className="w-4 h-4" />
        {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute bottom-12 right-0 bg-gray-900/95 text-white text-[10px] font-mono p-3 rounded-lg shadow-xl border border-gray-700 w-64 backdrop-blur-sm"
          >
            <div className="flex justify-between items-center mb-2 border-b border-gray-700 pb-2">
              <span className="font-bold text-xs">Onboarding Debug</span>
              <button onClick={refreshState} className="text-gray-400 hover:text-white">
                ↻
              </button>
            </div>

            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-400">Stage:</span>
                <span className={`font-bold ${getStageColor(state.computedStage)}`}>
                  {state.computedStage.toUpperCase()}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">Authenticated:</span>
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(state.isAuthenticated)}`} />
                  <span>{state.isAuthenticated ? 'YES' : 'NO'}</span>
                </div>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">DB onboarding:</span>
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(state.onboardingCompleted)}`} />
                  <span>{state.onboardingCompleted === null ? 'NULL' : state.onboardingCompleted ? 'DONE' : 'NO'}</span>
                </div>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">Welcome seen:</span>
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(state.welcomeSeen)}`} />
                  <span>{state.welcomeSeen ? 'YES' : 'NO'}</span>
                </div>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">Tour seen:</span>
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(state.tourSeen)}`} />
                  <span>{state.tourSeen ? 'YES' : 'NO'}</span>
                </div>
              </div>

              <div className="border-t border-gray-700 mt-2 pt-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Tour Active:</span>
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(state.tourActive)}`} />
                    <span>{state.tourActive ? 'YES' : 'NO'}</span>
                  </div>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-400">Tour Requested:</span>
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(state.tourRequested)}`} />
                    <span>{state.tourRequested ? 'YES' : 'NO'}</span>
                  </div>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-400">Transitioning:</span>
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(state.transitioning)}`} />
                    <span>{state.transitioning ? 'YES' : 'NO'}</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-700 mt-2 pt-2 text-[8px] text-gray-500">
                User: {state.userId?.slice(0, 8) ?? 'none'}...
                <br />
                Updated: {state.timestamp.slice(11, 19)}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
